import {ChangeDetectionStrategy, ChangeDetectorRef, Component} from '@angular/core';

import {MapService} from '../../services/map/map.service';
import {GraphicsService} from '../../services/graphics/graphics.service';
import {GoogleAnalyticsService} from '../../services/googleAnalytics/google-analytics.service';
import esri = __esri;

/**
 * Adds the marque or drag zoom button to the top left of the esri-ui
 */
@Component({
  selector: 'app-drag-zoom',
  templateUrl: './drag-zoom.component.html',
  styleUrls: ['./drag-zoom.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DragZoomComponent {
  public dragZoomActive = false;
  public currentView: any;
  private mouseDragEvent: any;
  private mouseUpEvent;
  private mouseDownEvent;
  private graphicsObj = {
    initialPt: null,
    tmpPoint: null,
    geom: [],
    drawingActive: false
  };
  private graphicsLayer: esri.GraphicsLayer;
  private polygon;

  constructor(private map: MapService, private graphics: GraphicsService,
              private ga: GoogleAnalyticsService, private cd: ChangeDetectorRef) { }

  /**
   * Element click event
   */
  public async onClick() {
    this.dragZoomActive = true;
    this.onMouseDown();
    if ( document.getElementsByTagName('app-root')) {
      document.getElementsByTagName('app-root')[0].className += 'marqueZoom';
      this.graphicsLayer = await this.graphics.addGraphicsLayer('Zoom');
      this.cd.detectChanges();
    }
  }

  /**
   * Controls mouse down event. Engages mouse drag and mouse up events, and sets the initial
   * geometry of the graphics object
   */
  private onMouseDown() {
    this.currentView = this.map.getViewRef();
    this.mouseDownEvent = this.currentView.on('pointer-down', null, (e: any) => {
      this.graphicsObj.initialPt = e;
      if (!this.mouseDragEvent) {
        this.onMouseDrag();
      }
    }, (error) => {
      this.ga.exception(error, false);
    });
    this.mouseUpEvent = this.currentView.on('pointer-up', null, () => {
      this.zoomTo();
    }, (error) => {
      this.ga.exception(error, false);
    });
  }

  /**
   * Controls the mouse drag event and continuously redraws the polygon as the user drags the mouse
   */
  private onMouseDrag() {
    this.mouseDragEvent = this.currentView.on('drag', null, async (e: any) => {
      try {
        const poly = {
          color: {r: 222, g: 98, b: 38, a: 0.3},
          style: 'none',
          outline: {
            color: {r: 0, g: 0, b: 0, a: 1.0},
            style: 'dash',
            width: 0.5,
            type: 'simple-line'
          }
        };
        // stops map from being dragged while tool is in use
        e.stopPropagation();
        const geom = [];
        const oldPt = {x: this.graphicsObj.initialPt.x, y: this.graphicsObj.initialPt.y};
        geom.push([this.currentView.toMap(oldPt).x, this.currentView.toMap(oldPt).y]);
        geom.push([this.currentView.toMap({x: oldPt.x, y: e.y}).x, this.currentView.toMap({x: oldPt.x, y: e.y}).y]);
        geom.push([this.currentView.toMap({x: e.x, y: e.y}).x, this.currentView.toMap({x: e.x, y: e.y}).y]);
        geom.push([this.currentView.toMap({x: e.x, y: oldPt.y}).x, this.currentView.toMap({x: e.x, y: oldPt.y}).y]);
        geom.push(geom[0]);
        this.graphicsLayer.removeAll();
        this.polygon = this.graphics.createPolygon(geom, poly, this.graphicsLayer);
      } catch (error) {
        this.ga.exception(error, false);
      }
    });
  }

  /**
   * Zooms to the polygon the user draws, clears the graphics on the screen and resets everything
   * and turns the marque zoom tool off
   */
  private async zoomTo() {
    try {
      const poly = await this.polygon;
      this.currentView.goTo({
        target:  poly,
        heading: 0,
      });
      this.graphicsLayer.removeAll();
      this.graphics.removeGraphicsLayer(this.graphicsLayer);
      document.getElementsByTagName('app-root')[0].classList.remove('marqueZoom');
      this.graphicsObj.initialPt = null;
      this.graphicsObj.tmpPoint = null;
      this.graphicsObj.geom = [];
      if (this.mouseDragEvent) {
        this.removeMouseDragEvent();
      }
      if (this.mouseUpEvent) {
        this.removeMouseUpEvent();
      }
      if (this.mouseDownEvent) {
        this.removeMouseDownEvent();
      }
      this.cd.detectChanges();
    } catch (error) {
      this.ga.exception(error, false);
    }
  }

  /**
   * Removes the various mouse events from the dom to stop blocking other tools
   */
  private removeMouseDragEvent() {
    this.mouseDragEvent.remove();
    this.mouseDragEvent = null;
  }

  private removeMouseUpEvent() {
    this.mouseUpEvent.remove();
    this.mouseUpEvent = null;
  }

  private removeMouseDownEvent() {
    this.mouseDownEvent.remove();
    this.mouseDownEvent = null;
  }
}
